/**
 * GalleryViewer — Image slideshow component for contextual media display.
 *
 * Renders a gallery of images with captions, dot indicators, arrow navigation,
 * swipe support (mobile), auto-rotate for 3+ images, and auto-dismiss.
 *
 * Flavor-agnostic: used by any flavor that registers a show_media handler.
 *
 * Options:
 *   images:     [{ url, caption? }]  — required
 *   title:      string               — optional header text
 *   autoRotate: boolean              — default true (only activates for 3+ images)
 *   timeout:    number               — auto-dismiss seconds, 0 = no auto-dismiss
 *   onDismiss:  function             — called when gallery is dismissed
 */
export class GalleryViewer {
  constructor(options = {}) {
    this.images = options.images || [];
    this.title = options.title || '';
    this.autoRotate = options.autoRotate !== false;
    this.timeout = options.timeout ?? 10;
    this.onDismiss = options.onDismiss || (() => {});

    this.current = 0;
    this.container = null;
    this.imageEl = null;
    this.captionEl = null;
    this.dotsEl = null;

    this._rotateInterval = null;
    this._dismissTimeout = null;
    this._interactionTimeout = null;

    this._dragging = false;
    this._startX = 0;
    this._swiped = false;
    this._dragThreshold = 40;

    this._boundTouchMove = this._onTouchMove.bind(this);
    this._boundTouchEnd = this._onTouchEnd.bind(this);
    this._boundMouseMove = this._onMouseMove.bind(this);
    this._boundMouseUp = this._onMouseUp.bind(this);
    this._dismissed = false;
  }

  mount(containerEl) {
    if (!containerEl || this.images.length === 0) return;
    this._buildDOM();
    containerEl.appendChild(this.container);
    this._setupDragEvents();
    this._startAutoRotate();
    this._startAutoDismiss();
  }

  dismiss() {
    if (this._dismissed) return;
    this._dismissed = true;
    this._stopAutoRotate();
    this._stopAutoDismiss();
    this._removeEventListeners();

    if (this.container) {
      this.container.classList.add('ttp-gallery--hiding');
      const c = this.container;
      setTimeout(() => {
        if (c.parentNode) c.parentNode.removeChild(c);
      }, 280);
    }
    this.container = null;
    this.onDismiss();
  }

  replace(config) {
    const parent = this.container?.parentNode;
    if (!parent) return;

    this._stopAutoRotate();
    this._stopAutoDismiss();
    this._removeEventListeners();
    if (this.container) this.container.remove();

    this.images = config.images || [];
    this.title = config.title || '';
    this.autoRotate = config.autoRotate !== false;
    this.timeout = config.timeout ?? 10;
    this.current = 0;
    this._dismissed = false;

    this._buildDOM();
    parent.appendChild(this.container);
    this._setupDragEvents();
    this._startAutoRotate();
    this._startAutoDismiss();
  }

  // ═══════════════════════════════════════════
  // DOM
  // ═══════════════════════════════════════════

  _buildDOM() {
    this.container = document.createElement('div');
    this.container.className = 'ttp-gallery';

    // Header (title + close)
    const header = document.createElement('div');
    header.className = 'ttp-gallery-header';

    const titleEl = document.createElement('span');
    titleEl.className = 'ttp-gallery-title';
    titleEl.textContent = this.title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'ttp-gallery-close';
    closeBtn.setAttribute('aria-label', 'Close gallery');
    closeBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    closeBtn.addEventListener('click', () => this.dismiss());
    header.appendChild(closeBtn);

    this.container.appendChild(header);

    // Slide area
    const slideArea = document.createElement('div');
    slideArea.className = 'ttp-gallery-slide-area';

    // Left arrow
    if (this.images.length > 1) {
      const leftArrow = document.createElement('button');
      leftArrow.className = 'ttp-gallery-arrow ttp-gallery-arrow--left';
      leftArrow.setAttribute('aria-label', 'Previous image');
      leftArrow.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
      leftArrow.addEventListener('click', () => {
        this._goTo(this.current - 1);
        this._pauseAutoRotate();
      });
      slideArea.appendChild(leftArrow);
    }

    // Image container
    const imgContainer = document.createElement('div');
    imgContainer.className = 'ttp-gallery-image-container';

    this.imageEl = document.createElement('img');
    this.imageEl.className = 'ttp-gallery-image';
    this.imageEl.alt = '';
    this.imageEl.draggable = false;
    imgContainer.appendChild(this.imageEl);

    slideArea.appendChild(imgContainer);

    // Right arrow
    if (this.images.length > 1) {
      const rightArrow = document.createElement('button');
      rightArrow.className = 'ttp-gallery-arrow ttp-gallery-arrow--right';
      rightArrow.setAttribute('aria-label', 'Next image');
      rightArrow.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;
      rightArrow.addEventListener('click', () => {
        this._goTo(this.current + 1);
        this._pauseAutoRotate();
      });
      slideArea.appendChild(rightArrow);
    }

    this.container.appendChild(slideArea);

    // Caption
    this.captionEl = document.createElement('div');
    this.captionEl.className = 'ttp-gallery-caption';
    this.container.appendChild(this.captionEl);

    // Dots
    if (this.images.length > 1) {
      this.dotsEl = document.createElement('div');
      this.dotsEl.className = 'ttp-gallery-dots';
      this.images.forEach((_, i) => {
        const dot = document.createElement('div');
        dot.className = 'ttp-gallery-dot';
        dot.addEventListener('click', () => {
          this._goTo(i);
          this._pauseAutoRotate();
        });
        this.dotsEl.appendChild(dot);
      });
      this.container.appendChild(this.dotsEl);
    }

    this._updateSlide();
  }

  _updateSlide() {
    const img = this.images[this.current];
    if (!img || !this.imageEl) return;

    this.imageEl.src = img.url;
    this.imageEl.alt = img.caption || '';

    if (this.captionEl) {
      this.captionEl.textContent = img.caption || '';
      this.captionEl.style.display = img.caption ? '' : 'none';
    }

    if (this.dotsEl) {
      const dots = this.dotsEl.querySelectorAll('.ttp-gallery-dot');
      dots.forEach((dot, i) => dot.classList.toggle('ttp-gallery-dot--active', i === this.current));
    }
  }

  _goTo(index) {
    if (this.images.length === 0) return;
    this.current = ((index % this.images.length) + this.images.length) % this.images.length;
    this._updateSlide();
  }

  // ═══════════════════════════════════════════
  // AUTO-ROTATE
  // ═══════════════════════════════════════════

  _startAutoRotate() {
    if (!this.autoRotate || this.images.length < 3) return;
    this._rotateInterval = setInterval(() => this._goTo(this.current + 1), 3000);
  }

  _stopAutoRotate() {
    if (this._rotateInterval) {
      clearInterval(this._rotateInterval);
      this._rotateInterval = null;
    }
    if (this._interactionTimeout) {
      clearTimeout(this._interactionTimeout);
      this._interactionTimeout = null;
    }
  }

  _pauseAutoRotate() {
    this._stopAutoRotate();
    if (!this.autoRotate || this.images.length < 3) return;
    this._interactionTimeout = setTimeout(() => {
      this._startAutoRotate();
    }, 5000);
  }

  // ═══════════════════════════════════════════
  // AUTO-DISMISS
  // ═══════════════════════════════════════════

  _startAutoDismiss() {
    if (this.timeout <= 0) return;
    this._dismissTimeout = setTimeout(() => this.dismiss(), this.timeout * 1000);
  }

  _stopAutoDismiss() {
    if (this._dismissTimeout) {
      clearTimeout(this._dismissTimeout);
      this._dismissTimeout = null;
    }
  }

  // ═══════════════════════════════════════════
  // SWIPE / DRAG
  // ═══════════════════════════════════════════

  _setupDragEvents() {
    if (this.images.length <= 1) return;

    const area = this.container.querySelector('.ttp-gallery-slide-area');
    if (!area) return;

    area.addEventListener('touchstart', (e) => {
      this._dragging = true;
      this._startX = e.touches[0].clientX;
      this._swiped = false;
    }, { passive: true });

    area.addEventListener('mousedown', (e) => {
      this._dragging = true;
      this._startX = e.clientX;
      this._swiped = false;
    });

    document.addEventListener('touchmove', this._boundTouchMove, { passive: true });
    document.addEventListener('touchend', this._boundTouchEnd);
    document.addEventListener('mousemove', this._boundMouseMove);
    document.addEventListener('mouseup', this._boundMouseUp);
  }

  _onTouchMove(e) {
    if (!this._dragging) return;
    const diff = e.touches[0].clientX - this._startX;
    if (Math.abs(diff) > this._dragThreshold) {
      this._goTo(this.current + (diff > 0 ? -1 : 1));
      this._startX = e.touches[0].clientX;
      this._swiped = true;
      this._pauseAutoRotate();
    }
  }

  _onTouchEnd() {
    this._dragging = false;
    if (this._swiped) {
      setTimeout(() => { this._swiped = false; }, 400);
    }
  }

  _onMouseMove(e) {
    if (!this._dragging) return;
    const diff = e.clientX - this._startX;
    if (Math.abs(diff) > this._dragThreshold) {
      this._goTo(this.current + (diff > 0 ? -1 : 1));
      this._startX = e.clientX;
      this._swiped = true;
      this._pauseAutoRotate();
    }
  }

  _onMouseUp() {
    this._dragging = false;
    if (this._swiped) {
      setTimeout(() => { this._swiped = false; }, 400);
    }
  }

  _removeEventListeners() {
    document.removeEventListener('touchmove', this._boundTouchMove);
    document.removeEventListener('touchend', this._boundTouchEnd);
    document.removeEventListener('mousemove', this._boundMouseMove);
    document.removeEventListener('mouseup', this._boundMouseUp);
  }

  // ═══════════════════════════════════════════
  // CSS (injected once per context)
  // ═══════════════════════════════════════════

  static _stylesInjected = new WeakSet();

  static injectStyles(root) {
    if (GalleryViewer._stylesInjected.has(root)) return;
    GalleryViewer._stylesInjected.add(root);

    const css = GalleryViewer.generateCSS();
    const existingStyle = root.querySelector('style');
    if (existingStyle) {
      existingStyle.textContent += '\n' + css;
    } else {
      const style = document.createElement('style');
      style.textContent = css;
      root.appendChild(style);
    }
  }

  static generateCSS() {
    return `
/* ═══════════════════════════════════════
   GALLERY VIEWER
   ═══════════════════════════════════════ */

.ttp-gallery {
  width: 100%;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  animation: ttpGalleryIn 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.ttp-gallery.ttp-gallery--hiding {
  animation: ttpGalleryOut 0.28s ease forwards;
  pointer-events: none;
}

@keyframes ttpGalleryIn {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ttpGalleryOut {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-10px); }
}

/* ── Header ── */

.ttp-gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px 6px;
}

.ttp-gallery-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ttp-gallery-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(0,0,0,0.06);
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s;
}
.ttp-gallery-close:hover {
  background: rgba(0,0,0,0.1);
}

/* ── Slide Area ── */

.ttp-gallery-slide-area {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: #f1f5f9;
  user-select: none;
  -webkit-user-select: none;
  cursor: grab;
  overflow: hidden;
}
.ttp-gallery-slide-area:active {
  cursor: grabbing;
}

.ttp-gallery-image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ttp-gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

/* ── Arrows ── */

.ttp-gallery-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255,255,255,0.85);
  color: #334155;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  transition: background 0.15s, transform 0.15s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.ttp-gallery-arrow:hover {
  background: #fff;
  transform: translateY(-50%) scale(1.05);
}
.ttp-gallery-arrow:active {
  transform: translateY(-50%) scale(0.95);
}
.ttp-gallery-arrow--left {
  left: 8px;
}
.ttp-gallery-arrow--right {
  right: 8px;
}

/* ── Caption ── */

.ttp-gallery-caption {
  padding: 6px 12px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Dots ── */

.ttp-gallery-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 4px 0 10px;
}

.ttp-gallery-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #cbd5e1;
  cursor: pointer;
  transition: all 0.25s;
  -webkit-tap-highlight-color: transparent;
}

.ttp-gallery-dot--active {
  width: 18px;
  border-radius: 4px;
  background: #3b82f6;
}

/* ── Mobile gallery (mounted to body) ── */

.ttp-gallery--mobile {
  position: fixed !important;
  left: 8px !important;
  right: 8px !important;
  z-index: 2147483645 !important;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
`;
  }

  static injectMobileStyles() {
    if (document.getElementById('ttp-gallery-mobile-styles')) return;
    const s = document.createElement('style');
    s.id = 'ttp-gallery-mobile-styles';
    s.textContent = GalleryViewer.generateCSS();
    document.head.appendChild(s);
  }

  static injectFullscreenStyles() {
    if (document.getElementById('ttp-gallery-fullscreen-viewer-styles')) return;
    const s = document.createElement('style');
    s.id = 'ttp-gallery-fullscreen-viewer-styles';
    s.textContent = GalleryViewer.generateCSS() + '\n' + GalleryViewer.generateFullscreenCSS();
    document.head.appendChild(s);
  }

  static generateFullscreenCSS() {
    return `
/* ═══════════════════════════════════════
   GALLERY — FULLSCREEN IMMERSIVE
   ═══════════════════════════════════════ */

.ttp-gallery--fullscreen {
  width: 100vw;
  height: 100vh;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  position: relative;
  animation: ttpGalleryFsIn 0.4s ease;
  overflow: hidden;
}

.ttp-gallery--fullscreen.ttp-gallery--hiding {
  animation: ttpGalleryFsOut 0.3s ease forwards;
}

@keyframes ttpGalleryFsIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes ttpGalleryFsOut {
  from { opacity: 1; }
  to   { opacity: 0; }
}

/* ── Header: floating top bar ── */

.ttp-gallery--fullscreen .ttp-gallery-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 16px 20px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5), transparent);
}

.ttp-gallery--fullscreen .ttp-gallery-title {
  font-size: 18px;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0,0,0,0.5);
}

.ttp-gallery--fullscreen .ttp-gallery-close {
  width: 40px;
  height: 40px;
  background: rgba(255,255,255,0.15);
  color: #fff;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.ttp-gallery--fullscreen .ttp-gallery-close:hover {
  background: rgba(255,255,255,0.25);
}
.ttp-gallery--fullscreen .ttp-gallery-close svg {
  width: 22px;
  height: 22px;
}

/* ── Slide area: fills entire viewport ── */

.ttp-gallery--fullscreen .ttp-gallery-slide-area {
  width: 100vw;
  height: 100vh;
  aspect-ratio: auto;
  background: transparent;
}

.ttp-gallery--fullscreen .ttp-gallery-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ── Arrows: large, floating on edges ── */

.ttp-gallery--fullscreen .ttp-gallery-arrow {
  width: 52px;
  height: 52px;
  background: rgba(255,255,255,0.12);
  color: #fff;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: none;
}
.ttp-gallery--fullscreen .ttp-gallery-arrow:hover {
  background: rgba(255,255,255,0.25);
  transform: translateY(-50%) scale(1.05);
}
.ttp-gallery--fullscreen .ttp-gallery-arrow svg {
  width: 28px;
  height: 28px;
}
.ttp-gallery--fullscreen .ttp-gallery-arrow--left { left: 16px; }
.ttp-gallery--fullscreen .ttp-gallery-arrow--right { right: 16px; }

/* ── Caption: floating bottom center ── */

.ttp-gallery--fullscreen .ttp-gallery-caption {
  position: absolute;
  bottom: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  font-size: 15px;
  color: #fff;
  text-shadow: 0 1px 6px rgba(0,0,0,0.6);
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 8px 20px;
  border-radius: 20px;
  max-width: 80vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Dots: floating bottom ── */

.ttp-gallery--fullscreen .ttp-gallery-dots {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  gap: 10px;
  padding: 6px 14px;
  background: rgba(0,0,0,0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 20px;
}
.ttp-gallery--fullscreen .ttp-gallery-dot {
  width: 10px;
  height: 10px;
  background: rgba(255,255,255,0.4);
}
.ttp-gallery--fullscreen .ttp-gallery-dot--active {
  width: 28px;
  border-radius: 5px;
  background: #fff;
}

/* ── Mobile tweaks ── */

@media (max-width: 768px) {
  .ttp-gallery--fullscreen .ttp-gallery-arrow {
    width: 40px;
    height: 40px;
  }
  .ttp-gallery--fullscreen .ttp-gallery-arrow--left { left: 8px; }
  .ttp-gallery--fullscreen .ttp-gallery-arrow--right { right: 8px; }
  .ttp-gallery--fullscreen .ttp-gallery-caption {
    font-size: 13px;
    bottom: 50px;
  }
}
`;
  }
}
